import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { ComposableMap, Geographies, Geography, Marker, Sphere } from "react-simple-maps";
import worldTopo from "world-atlas/countries-110m.json";

const EASE = [0.16, 1, 0.3, 1];

const DISHES = [
  { month: "Jan", full: "January", dish: "Calçots", place: "Valls, Catalonia, Spain", coords: [1.25, 41.29], hasImage: true, note: "We start the year in Catalonia to celebrate the Calçotada. This is more than a meal; it is a ritual of smoke, charred onions, and communal messy eating that serves as a cornerstone of Catalan identity. Grapevine char only." },
  { month: "Feb", full: "February", dish: "Tamales", place: "CDMX, Mexico", coords: [-99.13, 19.43], hasImage: true, note: "On February 2 (Día de la Candelaria), those who found the Niño Dios figurine in the January 6 Rosca de Reyes have to “pay their debt” by hosting a large tamal feast in Mexico City. The official end of the Christmas season means cooking, sharing and eating with family and friends." },
  { month: "Mar", full: "March", dish: "Maple Syrup", place: "Québec, Canada", coords: [-71.21, 46.81], hasImage: true, note: "For March, we head to Quebec — where I happen to live. The first good syrup usually appears around March 5–15, depending on the freeze-thaw cycle. Early-season syrup is pale, golden and floral; by late March, it gets darker, richer and more savory." },
  { month: "Apr", full: "April", dish: "White Asparagus", place: "Beelitz, Germany", coords: [12.97, 52.24], hasImage: true, note: "April brings Europe's “White Gold”: Beelitzer Spargel, Germany's prized white asparagus. The season is just getting started, with Spargelzeit running through June 24 — when, with typically German punctuality, the asparagus season abruptly ends." },
  { month: "May", full: "May", dish: "Caș & Brânză de Burduf", short: "Caș & Brânză", place: "Carpathian Mountains, Romania", coords: [25.5, 45.4], hasImage: true, note: "In May we travel into the Romanian side of the Carpathians, when shepherds move their flocks into the high mountain pastures and begin making fresh sheep's cheese. The milk changes immediately, infused by the hundreds of wildflowers and herbs the sheep graze along the way." },
  { month: "Jun", full: "June", dish: "Alphonso Mango", place: "Ratnagiri, India", coords: [73.31, 16.99], hasImage: true, note: "While mangoes exist globally, the true Ratnagiri Alphonso is a short-lived Indian obsession. For a few precious weeks in June, Ratnagiri revolves around the fruit; it's the time to eat Aamras made from perfectly ripe local mangoes, before the season disappears again." },
  { month: "Jul", full: "July", dish: "Cavaillon Melon", place: "Provence, France", coords: [5.04, 43.84], hasImage: true, note: "Cavaillon melon (Provence) — the sugar-density peak of July. If the fruit doesn't smell like a perfume shop from three feet away, it's just a cantaloupe. Local lore dictates that a perfect melon has exactly 10 dark green vertical stripes. 9 is premature; 11 is a fermentation risk." },
  { month: "Aug", full: "August", dish: "Hatch Green Chiles", place: "New Mexico, USA", coords: [-107.15, 32.67], hasImage: true, note: "In August, we head to New Mexico for Hatch Green Chile. The scent of charred skins and propane from Mesilla Valley meets Santa Fe fine dining, where chile gets grand-cru treatment. Authentic Hatch chile is flame-roasted until blistered and ready to slip its skin." },
  { month: "Sep", full: "September", dish: "Matsutake Mushrooms", short: "Matsutake", place: "Kyoto, Japan", coords: [135.77, 35.01], hasImage: true, note: "These are the “truffles of the East.” They cannot be cultivated. The scent is ephemeral — highly spicy and woody. Eating them grilled over charcoal in a Kyoto ryokan during these weeks of September is a pinnacle food experience, a Japanese atmospheric event." },
  { month: "Oct", full: "October", dish: "Female Hairy Crab Roe", short: "Hairy Crab Roe", place: "Shanghai, China", coords: [121.47, 31.23], hasImage: true, note: "By late September, the female crab's roe is still soft and semi-liquid. By October, it reaches its peak: dense, granular, intensely flavored, and deep orange-red. The roe should hold its shape when the shell is opened, with the firm, crumbly texture of a hard-boiled egg yolk." },
  { month: "Nov", full: "November", dish: "White Truffle", place: "Alba, Italy", coords: [8.03, 44.7], hasImage: true, note: "It's an expensive, fleeting burst of aroma, but in November, this corner of Alba in Piedmont is the only place that matters — tuber magnatum pico, the legendary white truffle at its aromatic peak. Eat them there: tajarin with butter, truffle shaved tableside, weighed before and after — no kitchen substitutions." },
  { month: "Dec", full: "December", dish: "Lutefisk", place: "Trondheim, Norway", coords: [10.4, 63.43], hasImage: true, note: "Before refrigeration, Norway learned to preserve the ocean itself. In the Arctic winter, cod was transformed by wind, time and tradition into December lutefisk — the controversial but beloved centerpiece of Christmas tables." },
];

const RECIPES = {
  0: {
    title: "Authentic Romesco Sauce",
    img: "/images/recipe-romesco.jpg",
    meta: "Yield ~2 cups · Prep 20 min · Cook 40 min",
    ingredients: [
      "2 large ripe tomatoes",
      "1 whole head of garlic",
      "2 dried ñora peppers (or ancho peppers)",
      "½ cup toasted almonds",
      "½ cup toasted hazelnuts, skinned",
      "1 thick slice of crusty stale bread, fried in olive oil",
      "½ cup extra virgin olive oil",
      "2 tbsp sherry vinegar",
      "1 tsp smoked paprika (pimentón)",
      "1 tsp salt, or to taste",
    ],
    steps: [
      "Soak the ñora peppers in hot water for 30 minutes. Discard stems and seeds, scrape the flesh from the skins; discard the skins.",
      "Heat the oven to 200°C (400°F). Slice the top quarter off the garlic head and roast with the whole tomatoes for 35–40 minutes, until soft and charred. Let cool.",
      "Peel the roasted tomatoes and squeeze the garlic cloves out of their skins.",
      "Pulse the almonds, hazelnuts and fried bread to a coarse meal.",
      "Add the pepper flesh, tomatoes, garlic, sherry vinegar, paprika and salt. Blend until well combined.",
      "With the processor running, slowly drizzle in the olive oil until a thick, textured paste forms. Adjust salt and vinegar to taste.",
    ],
  },
  1: {
    title: "Pork Tamales in Red Chile",
    img: "/images/recipe-tamales.jpg",
    meta: "Makes 20–30 · Steam 60–90 min",
    ingredients: [
      "20–30 dried corn husks",
      "4 cups masa harina (Maseca)",
      "⅔ cup softened lard or vegetable shortening",
      "1 tsp salt",
      "2½–3 cups warm chicken broth",
      "2 cups cooked and shredded pork",
      "1 cup red chile sauce or salsa",
    ],
    steps: [
      "Soak the corn husks in hot water for 30 minutes until soft and pliable.",
      "Beat the lard until fluffy. Mix in the masa harina and salt, then slowly add warm broth until the dough is soft and spreadable, like thick frosting.",
      "Spread a 2-inch scoop of masa onto the wide end of a soaked husk. Spoon a tablespoon of pork and red chile down the center.",
      "Fold the sides over to form a cylinder, then fold the bottom end up.",
      "Steam upright, open-end up, over boiling water on medium-low heat for 60–90 minutes, until the masa pulls away easily from the husk.",
    ],
  },
  2: {
    title: "Tire sur la Neige (Maple Taffy on Snow)",
    img: "/images/recipe-maple.jpg",
    meta: "Sugar-shack classic · Boil to 115°C (238°F)",
    ingredients: [
      "Pure Quebec amber maple syrup — as much as you dare",
      "A trough of fresh, clean, packed snow",
      "Wooden craft sticks",
      "A candy thermometer",
    ],
    steps: [
      "Boil the maple syrup to 115°C (238°F).",
      "Pour it in thin ribbons over the fresh, clean snow.",
      "Wait a few seconds until the ribbons firm up, then roll them onto wooden craft sticks.",
      "Eat immediately, ideally still standing in the sugar shack's backyard.",
    ],
  },
  3: {
    title: "Asperges Blanches",
    img: "/images/recipe-asparagus.png",
    meta: "Serves 4 · Simmer 15 min",
    ingredients: [
      "1 kg white asparagus spears",
      "Salted water with lemon, a knob of butter and a pinch of sugar",
      "Béarnaise or brown butter, to serve",
      "Fresh chervil",
    ],
    steps: [
      "Peel the stalks.",
      "Simmer 15 minutes in the salted water with lemon, butter and sugar, until tender.",
      "Serve warm with béarnaise or brown butter and fresh chervil.",
    ],
  },
  5: {
    title: "Mango Lassi",
    img: "/images/recipe-lassi.png",
    meta: "Serves 2 · Prep 10 min",
    ingredients: [
      "2 ripe Alphonso mangoes, pureed",
      "1 cup plain yogurt",
      "½ cup cold milk",
      "Sugar or honey, to taste",
      "A pinch of ground cardamom",
      "Saffron strands and chopped pistachios, to garnish",
    ],
    steps: [
      "Blend the mango puree, yogurt, milk and sugar until smooth.",
      "Add the cardamom and a few saffron strands; blend again briefly.",
      "Serve cold, topped with pistachios and saffron.",
    ],
  },
  6: {
    title: "Cavaillon Melon with Sea Salt",
    img: "/images/recipe-melon.jpg",
    meta: "No cooking · Serve chilled",
    ingredients: [
      "1 perfectly ripe Cavaillon melon — ripe when the stem dries and the rind turns golden",
      "A pinch of sea salt",
    ],
    steps: [
      "Cut the melon into wedges and scoop out the seeds.",
      "Serve chilled, with a pinch of sea salt.",
    ],
  },
  10: {
    title: "Tagliolini al Tartufo Bianco",
    img: "/images/recipe-truffle.png",
    meta: "Serves 2 · 10 min",
    ingredients: [
      "Fresh tagliolini",
      "Cultured butter, melted",
      "A ladle of hot pasta water",
      "Fresh white Alba truffle",
      "Coarse sea salt",
    ],
    steps: [
      "Toss the fresh tagliolini in melted butter and hot pasta water until silky.",
      "Shave the fresh white truffle generously over the top, immediately before serving.",
      "Season with coarse sea salt.",
    ],
  },
  11: {
    title: "Lutefisk Tradisjonell",
    img: "/images/recipe-lutefisk.png",
    meta: "Serves 4–6 · Bake 25–30 min",
    ingredients: [
      "Prepared lutefisk, ready to cook",
      "Coarse salt",
      "Boiled potatoes",
      "Crispy pork fat bits (spekk)",
      "Melted butter — plenty of it",
      "Pea purée and lefse, alongside",
    ],
    steps: [
      "Salt the lutefisk and leave it to rest.",
      "Bake at 225°C for 25–30 minutes, until it flakes.",
      "Serve straight away with potatoes, crispy pork bits and plenty of melted butter.",
    ],
  },
};

const STAR_PATH =
  "M0,-10 L2.4,-3.1 L10,-3.1 L3.8,1.2 L6.2,8.6 L0,4 L-6.2,8.6 L-3.8,1.2 L-10,-3.1 L-2.4,-3.1 Z";

const anchorFor = (lon) => (lon > 110 ? "end" : lon < -90 ? "start" : "middle");

const labelStyle = {
  fontFamily: "Archivo, sans-serif",
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  paintOrder: "stroke",
};

export default function WorldMap() {
  const [active, setActive] = useState(0);
  const [recipeOpen, setRecipeOpen] = useState(false);
  const current = DISHES[active];
  const recipe = RECIPES[active];

  useEffect(() => setRecipeOpen(false), [active]);

  const handleStarSelect = (i) => {
    setActive(i);
    if (!DISHES[i].hasImage) return;
    const el = document.querySelector(`[data-testid="gallery-card-${i}"]`);
    if (!el) return;
    if (window.__lenis) {
      window.__lenis.scrollTo(el, { offset: -140, duration: 1.4 });
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    window.dispatchEvent(new CustomEvent("bookata:highlight-month", { detail: i }));
  };

  return (
    <section
      id="map"
      data-testid="map-section"
      className="relative overflow-hidden border-y-4 border-navy bg-punch px-6 py-24 md:px-12 md:py-32"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: "radial-gradient(#2E3192 1.5px, transparent 1.5px)",
          backgroundSize: "28px 28px",
        }}
      />
      <div className="relative mx-auto max-w-[1280px]">
        <motion.p
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-[11px] font-bold uppercase tracking-[0.4em] text-banana"
        >
          The 2027 atlas
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.75, delay: 0.08, ease: EASE }}
          className="mt-5 font-display text-[clamp(2.4rem,5.5vw,5rem)] font-extrabold uppercase leading-[0.92] tracking-tight text-navy"
        >
          Twelve dishes.
          <br />
          Twelve places.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.16, ease: EASE }}
          className="mt-5 max-w-xl font-serif text-xl italic text-navy/85"
        >
          Hover a star to peek at the dish, tap to open its story — every star leaps straight
          to its illustration below.
        </motion.p>

        <div className="mt-14 grid items-start gap-10 lg:grid-cols-[1.5fr_1fr]">
          <motion.div
            initial={{ opacity: 0, y: 56 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="min-w-0 border-4 border-navy bg-paper p-3 shadow-[10px_10px_0_#2E3192] md:p-5"
          >
            <div className="scrollbar-hide overflow-x-auto">
              <ComposableMap
                data-testid="world-map"
                projection="geoEqualEarth"
                projectionConfig={{ scale: 168, center: [10, 4] }}
                width={800}
                height={460}
                className="h-auto w-full min-w-[600px]"
              >
                <Sphere id="bookata-sphere" fill="#83C7E6" stroke="#2E3192" strokeWidth={1.5} />
                <Geographies geography={worldTopo}>
                  {({ geographies }) =>
                    geographies.map((geo) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill="#2E3192"
                        stroke="#FFF8EC"
                        strokeWidth={0.4}
                        style={{
                          default: { outline: "none" },
                          hover: { outline: "none", fill: "#3a3fa8" },
                          pressed: { outline: "none" },
                        }}
                      />
                    ))
                  }
                </Geographies>
                {DISHES.map((d, i) => (
                  <Marker key={d.dish} coordinates={d.coords}>
                    <g
                      onClick={() => handleStarSelect(i)}
                      className="group cursor-pointer"
                    >
                      <circle data-testid={`map-star-${i}`} r="18" fill="transparent" />
                      {i === active && (
                        <circle className="star-ring" r="11" fill="none" stroke="#F2E85C" strokeWidth="2" />
                      )}
                      <path
                        d={STAR_PATH}
                        fill={i === active ? "#F2629E" : "#F2E85C"}
                        stroke="#2E3192"
                        strokeWidth="1.6"
                        transform={i === active ? "scale(1.45)" : "scale(1.15)"}
                      />
                      {i !== active && (
                        <text
                          y={-22}
                          textAnchor={anchorFor(d.coords[0])}
                          className="pointer-events-none opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                          fill="#2E3192"
                          stroke="#FFF8EC"
                          strokeWidth="3.5"
                          style={labelStyle}
                        >
                          {d.dish}
                        </text>
                      )}
                    </g>
                  </Marker>
                ))}
                <Marker key={`label-${active}`} coordinates={current.coords}>
                  <text
                    y={-22}
                    textAnchor={anchorFor(current.coords[0])}
                    fill="#2E3192"
                    stroke="#FFF8EC"
                    strokeWidth="3.5"
                    style={labelStyle}
                  >
                    {current.dish}
                  </text>
                </Marker>
              </ComposableMap>
            </div>
            <p className="mt-3 text-center text-[10px] font-bold uppercase tracking-[0.25em] text-navy/50 md:hidden">
              Swipe the map
            </p>
          </motion.div>

          <div className="flex min-w-0 flex-col gap-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35, ease: EASE }}
                data-testid="map-info-panel"
                className="border-4 border-navy bg-paper p-8 shadow-[10px_10px_0_#2E3192]"
              >
                <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-punch">
                  {String(active + 1).padStart(2, "0")} — {current.full}
                </p>
                <p
                  data-testid="map-info-dish"
                  className="mt-3 font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-navy md:text-5xl"
                >
                  {current.dish}
                </p>
                <p data-testid="map-info-place" className="mt-2 font-serif text-xl italic text-navy/80">
                  {current.place}
                </p>
                <p data-testid="map-info-note" className="mt-4 border-t-2 border-navy/15 pt-4 text-sm leading-relaxed text-navy/75">
                  {current.note}
                </p>
                {recipe && (
                  <>
                    <button
                      type="button"
                      data-testid="map-recipe-toggle"
                      onClick={() => setRecipeOpen((o) => !o)}
                      className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-navy bg-banana px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-navy transition-colors duration-300 hover:bg-navy hover:text-banana"
                    >
                      {recipeOpen ? "Hide the recipe" : "Cook it — full recipe"}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-300 ${recipeOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {recipeOpen && (
                        <motion.div
                          key="recipe"
                          data-testid="map-recipe-card"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.45, ease: EASE }}
                          className="overflow-hidden"
                        >
                          <img
                            src={recipe.img}
                            alt={`${recipe.title} — hand-painted recipe card`}
                            data-testid="map-recipe-image"
                            className="mt-5 w-full border-2 border-navy"
                          />
                          <p className="mt-5 font-display text-lg font-extrabold uppercase tracking-tight text-navy">
                            {recipe.title}
                          </p>
                          <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em] text-punch">
                            {recipe.meta}
                          </p>
                          <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.25em] text-navy/60">
                            Ingredients
                          </p>
                          <ul className="mt-2 space-y-1.5 text-sm leading-relaxed text-navy/85">
                            {recipe.ingredients.map((ing) => (
                              <li key={ing} className="flex gap-2.5">
                                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-punch" />
                                {ing}
                              </li>
                            ))}
                          </ul>
                          <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.25em] text-navy/60">
                            Method
                          </p>
                          <ol className="mt-2 space-y-2.5 text-sm leading-relaxed text-navy/85">
                            {recipe.steps.map((s, idx) => (
                              <li key={s} className="flex gap-2.5">
                                <span className="font-display font-extrabold text-punch">{idx + 1}.</span>
                                {s}
                              </li>
                            ))}
                          </ol>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="flex flex-wrap gap-2">
              {DISHES.map((d, i) => (
                <button
                  key={d.dish}
                  type="button"
                  data-testid={`map-chip-${i}`}
                  onClick={() => setActive(i)}
                  onMouseEnter={() => setActive(i)}
                  className={`rounded-full border-2 border-navy px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors duration-200 ${
                    i === active ? "bg-navy text-banana" : "bg-paper text-navy hover:bg-banana"
                  }`}
                >
                  {d.month} · {d.short || d.dish}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
